import { Check, MoreVert } from "@mui/icons-material";
import { Checkbox, DialogActions, FormControlLabel, IconButton, Menu, MenuItem } from "@mui/material";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import { DataGrid } from "@mui/x-data-grid/DataGrid";
import { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import dayjs from "dayjs";
import { getAuth } from "firebase/auth";
import { child, get, getDatabase, ref, remove, set } from "firebase/database";
import { StorageReference, deleteObject, getDownloadURL, getStorage, listAll, ref as storageRef } from "firebase/storage";
import { enqueueSnackbar } from "notistack";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { EventType } from "rx/fbListSlices";
import { execWithProgress } from "rx/saveInProgressSlice";
import { AppDispatch, useTSelector } from "rx/store";
import { fetchUidData } from "rx/uidmapSlice";
import { archiveEvent, clearEvent, restoreDeleted } from "utils/archive";
import { useRFirebaseDb } from "utils/FirebaseDbWrapper";
import { useNL } from "utils/NLContext";
import { argouid } from "utils/SmallUtils";

export const useAppDispatch: () => AppDispatch = useDispatch;

export const DisplayNameOfUid: React.FC<{ uid: string }> = ({ uid }) => {
  const uidmap = useTSelector((state) => state.uidmap);
  const dispatch = useAppDispatch();
  const entry = uidmap[uid];
  if (!entry) {
    dispatch(fetchUidData({ uids: [uid], eventid: "TallinaTest" }));
    return <span>{uid}</span>;
  }
  return <span>{entry.displayName || entry.email || entry.phoneNumber}</span>;
};

const IsBufferEqual = (a: ArrayBuffer, b: ArrayBuffer) => {
  if (a.byteLength !== b.byteLength) return false;
  const view1 = new Uint8Array(a);
  const view2 = new Uint8Array(b);

  let equal = true;
  for (let i = 0; i < view1.length; i++) {
    if (view1[i] !== view2[i]) {
      equal = false;
      break;
    }
  }
  return equal;
}

const checkStorage = async (ref: StorageReference, backupurl: string, update: (incProgress?: boolean, incMax?: number, msg?: string) => boolean) => {
  const listing = await listAll(ref);
  if (update(false, listing.items.length + listing.prefixes.length)) return true;

  for (let pref of listing.prefixes) {
    if (await checkStorage(pref, `${backupurl}/${pref.name}`, update)) {
      return true;
    }
    if (update(true, 0, `Completed prefix ${pref.name}`)) return true;
  }
  for (let pref of listing.items) {
    const url = await getDownloadURL(pref);
    const response = await fetch(url);
    const data = await response.arrayBuffer();
    const burl = `${backupurl}/${pref.name}`;
    try {
      const bresponse = await fetch(burl);
      if (!bresponse.ok) {
        console.error(`Failed to fetch ${burl}: ${bresponse.status} ${bresponse.statusText}`);
        update(false, 0, `****** From backup, ${pref.name} failed: ${bresponse.status} ${bresponse.statusText}******`);
      } else {
        const bdata = await bresponse.arrayBuffer();
        if (IsBufferEqual(data, bdata)) {
          deleteObject(pref);
        } else {
          console.log("Buffers not equal raport it");
          update(false, 0, `****** From backup, ${pref.name} is different ******`);
        }
      }
      if (update(true, 0, `Completed item ${pref.name}`)) return true;
    } catch (err) {
      console.error(`Network error while fetching ${burl}:`, err);
    }
  }
}

const CleanupDialog: React.FC<{
  open: boolean;
  evid: string;
  onDone: () => void;
}> = ({ open, evid, onDone }) => {
  const [progressValue, setProgressValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");

  useEffect(() => {
    let effectstopped = false;
    if (open) {
      const runTask = async () => {
        const update = (incProgress?: boolean, incMax?: number, msg?: string) => {
          if (incProgress === undefined || incProgress) setProgressValue((v) => v + 1);
          if (incMax && incMax > 0) setMaxValue(v => v + incMax);
          if (msg) setProgressMsg(v => msg + "\n" + v);
          return effectstopped;
        }

        !effectstopped && (await checkStorage(storageRef(getStorage(), `/tracks/${evid}`), `https://archive.nutilogi.ee/backupTracks/oldtracks/${evid}`, update));
        !effectstopped && (await checkStorage(storageRef(getStorage(), `/nutilogi/events/${evid}`), `https://archive.nutilogi.ee/backupTracks/tracks/${evid}`, update));
        !effectstopped && (await checkStorage(storageRef(getStorage(), `/archive/${evid}`), `https://archive.nutilogi.ee/${evid}`, update));

        setProgressMsg(v => "===================\n== Cleaning complete\n===================\n" + v)
      }
      runTask();
    }
    return () => { effectstopped = true; }
  }, [evid, open, onDone]);

  return <Dialog
    open={open}
    maxWidth="md"
    fullWidth
  >
    <DialogTitle>Cleaning event {evid}</DialogTitle>
    <DialogContent>
      <LinearProgress value={maxValue > 0 ? progressValue * 100 / maxValue : 0} variant="determinate" />
      <TextField
        fullWidth
        multiline
        value={progressMsg}
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
      />
    </DialogContent>
    <DialogActions><Button variant="contained" onClick={() => {
      setProgressMsg("");
      setProgressValue(0);
      setMaxValue(0);
      onDone();
    }}>Close</Button></DialogActions>
  </Dialog>
}

const ArchiveMoreMenu: React.FC<{ evid: string, arch: string }> = ({ evid, arch }) => {
  const [doCleanup, setDoCleanup] = useState(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const openStoreUrl = useCallback((prefix: string) => {
    window.open(`https://console.firebase.google.com/project/nutilogi/storage/nutilogi.appspot.com/files/~2F${prefix}~2F${evid}`, "_blank");
    handleClose();
  }, [evid]);
  const openFBUrl = useCallback((prefix: string) => {
    window.open(`https://console.firebase.google.com/project/nutilogi/database/nutilogi/data/${prefix}/${evid}`, "_blank");
    handleClose();
  }, [evid]);


  return (
    <>
      <IconButton
        aria-label="more"
        aria-controls={open ? 'more-menu' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id="more-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleClose}>Unarchive (N/A)</MenuItem>
        <MenuItem onClick={() => { setDoCleanup(true); handleClose(); }}>Cleanup</MenuItem>
        <MenuItem onClick={() => openStoreUrl('archive')}> -&gt; A1 archive</MenuItem>
        <MenuItem onClick={() => openStoreUrl('tracks')}> -&gt; Old track in storage</MenuItem>
        <MenuItem onClick={() => openStoreUrl('nutilogi~2Fevents')}> -&gt; WS tracks</MenuItem>
        <MenuItem onClick={() => {
          window.open(`https://console.firebase.google.com/project/nutilogi/firestore/databases/-default-/data/~2Fevents~2F${evid}`, "_blank");
          handleClose();
        }}> -&gt; Firestore doc</MenuItem>
        <MenuItem onClick={() => {
          window.open(`https://console.firebase.google.com/project/nutilogi/database/nutilogi/data/events/${evid}`, "_blank");
          handleClose();
        }}> -&gt; Event list entry</MenuItem>
        <MenuItem onClick={() => openFBUrl('eventsdata')}> -&gt; Event data entry</MenuItem>
      </Menu>
      {<CleanupDialog evid={evid} onDone={() => setDoCleanup(false)} open={doCleanup} />}
    </>
  );
}

const moveToA2 = (evid: string): Promise<boolean> => {
  return getAuth()
    .currentUser?.getIdToken() // Can we still use this or should use getIdToken function
    .then((token) => {
      return fetch("https://kodu.vessmann.ee:7575", {
        headers: {
          Authorization: "Bearer " + token,
          "X-EventId": evid,
        },
      }).then(result => result.json()).then(data => {
        console.log("Got back", data);
        if (data.ok) {
          enqueueSnackbar(evid + ' moved to A2', { variant: "success" });
          return true;
        } else {
          console.log("Something failed", data);
          enqueueSnackbar('Failed to move event to A2', { variant: "error" });
          return false;
        }
      })
    }) ?? Promise.resolve(false);
}

const ArchiveProgressDialog: React.FC<{
  archiveid: string | undefined;
  unarchiveid: string | undefined;
  onDone: () => void;
}> = ({ archiveid, unarchiveid, onDone }) => {
  const [title, setTitle] = useState(
    archiveid ? `Archiving ${archiveid} ...` : `Unarchiving ${unarchiveid} ...`
  );
  const [progressValue, setProgressValue] = useState(0);
  const archiveInProgress = useRef<boolean>(false);
  const [progressMsg, setProgressMsg] = useState("");

  //const progressMsg = useRef<string>("");
  const { fbSet, fbRemove } = useNL();
  const dispatch = useAppDispatch();
  const updateProgress = useCallback((value: number, msg?: string) => {
    if (msg) {
      setProgressMsg((state) => {
        return msg + " - DONE\n" + state;
      });
    }
    //if (msg) progressMsg.current = msg + " - DONE\n" + progressMsg.current;
    setProgressValue(value);
  }, []);

  useEffect(() => {
    if (archiveid) {
      if (archiveInProgress.current) return;
      archiveInProgress.current = true;
      dispatch(
        execWithProgress(async () =>
          archiveEvent(archiveid, "archive", updateProgress)
            .then(async (wassuccess) => {
              if (wassuccess == null) return Promise.resolve();
              console.log("Archive result", wassuccess);
              setProgressValue(0);
              setTitle(`Cleaning ${archiveid} ...`);
              await clearEvent(archiveid, updateProgress, false);
              await fbSet(`/events/${archiveid}/arch`, "a1");
              await wassuccess.admincleanup();
            })
            .then(() => onDone())
        )
      );
    }
    if (unarchiveid) {
      dispatch(
        execWithProgress(async () => {
          const r = await restoreDeleted("archive", unarchiveid, true);
          if (r) fbRemove(`/events/${unarchiveid}/arch`);
          onDone();
        })
      );
    }
  }, [
    archiveid,
    dispatch,
    fbRemove,
    fbSet,
    onDone,
    unarchiveid,
    updateProgress,
  ]);

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <LinearProgress value={progressValue} variant="determinate" />
        <TextField
          fullWidth
          multiline
          value={progressMsg}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
      </DialogContent>
    </>
  );
};
const EventsManager: React.FC = () => {
  const events = useRFirebaseDb<{ [evid: string]: EventType }>("events");
  const unpaidevents = useRFirebaseDb<{ [evid: string]: string }>(
    "admin/unpaid"
  );
  const uidmap = useTSelector((state) => state.uidmap);
  const [unarchiveevid, setUnarchiveevid] = useState<string>();
  const [archiveevid, setArchiveevid] = useState<string>();
  const [eventsWithA1Files, setEventsWithA1Files] = useState<string[]>([]);
  const [a1FilesChecked, seta1FilesCheckde] = useState(false);
  const dispatch = useAppDispatch();
  const { fbSet, fbRemove } = useNL();

  const eventslist = useMemo(() => {
    if (!events) return [];
    const evlist = Object.entries(events).map(([evid, evdata]) => {
      const owner = Object.entries(evdata.admins || {}).find(
        ([uid, type]) => type === "owner"
      );
      return Object.assign(
        { id: evid, owner: owner ? owner[0] : "No owner" },
        evdata
      );
    });
    console.log(a1FilesChecked, eventsWithA1Files);
    if (a1FilesChecked)
      return evlist.filter(e => eventsWithA1Files.includes(e.id));
    return evlist;
  }, [events, a1FilesChecked, eventsWithA1Files]);
  const columns: GridColDef<(typeof eventslist)[number]>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 150,
    },
    {
      field: "starttime",
      headerName: "Date",
      filterable: false,
      width: 150,
      renderCell: (par) => {
        return dayjs(par.value).format("DD. MMM YYYY");
      },
    },
    { field: "name", headerName: "Name", width: 300 },
    {
      field: "owner",
      headerName: "Owner",
      width: 300,
      valueGetter: (value) => {
        const uentry = uidmap[value];
        if (uentry)
          return (
            uentry.displayName ||
            uentry.email ||
            uentry.phoneNumber ||
            "empty user"
          );
        dispatch(fetchUidData({ uids: [value], eventid: "--" }));
        return value;
      },
    },
    {
      field: "unpaid",
      headerName: "Unpaid",
      valueGetter: (_, row) => {
        if (unpaidevents === undefined) return undefined;
        return unpaidevents && unpaidevents[row.id] ? "unpaid" : "paid";
      },
      renderCell: (param) => {
        if (param.value === undefined) return null;
        const pref = child(ref(getDatabase(), "/admin/unpaid/"), param.row.id);
        return param.value === "unpaid" ? (
          <Chip
            onClick={() => {
              fbRemove(pref, param.row.name + " marked as unpaid");
            }}
            variant="outlined"
            color="error"
            sx={{ color: "red" }}
            label="Unpaid"
            size="small"
          />
        ) : (
          <Chip
            onClick={() => {
              fbSet(
                pref,
                "Manual: " + dayjs().toISOString(),
                param.row.name + " marked as paid"
              );
            }}
            variant="outlined"
            color="info"
            sx={{ color: "green" }}
            label="Ok"
            size="small"
            icon={<Check />}
          />
        );
      },
    },
    {
      field: "open",
      headerName: "Open",
      renderCell: (par) => {
        return (
          <Button
            onClick={() => {
              window
                .open("https://nutilogi.eu/" + par.row.id, "_evmanevent")
                ?.focus();
            }}
          >
            Open
          </Button>
        );
      },
    },
    {
      field: "result",
      headerName: "Result",
      renderCell: (par) => {
        return (
          <Button
            onClick={() => {
              window
                .open("https://nutilogi.eu/" + par.row.id + "/result", "_evmanevent")
                ?.focus();
            }}
          >
            Result
          </Button>
        );
      },
    },
    {
      field: "archived",
      headerName: "Archived",
      width: 150,
      align: "center",
      renderCell: (param) => {
        if (param.row.arch === "a2") {
          return <ArchiveMoreMenu evid={param.row.id} arch={param.row.arch} />
        }
        const evid = param.row.id;
        const text = param.row.arch ? param.row.arch === "a1" ? "A1 => A2" : "Unarchive" : "Archive";
        const color = param.row.arch === "a2" ? "inherit" : "primary";
        const onClick = param.row.arch
          ? param.row.arch === "a1" ? () => moveToA2(evid).then(async v => {
            const db = getDatabase();
            const argoadmref = ref(db, `/events/${evid}/admins/${argouid}`);
            const snap = await get(argoadmref);
            if (!snap.exists()) {
              await set(argoadmref, "ArgoTMP");
            }
            await fbSet(`/events/${evid}/arch`, "a2");
            if (!snap.exists()) {
              await remove(argoadmref);
            }
          }) : async () => {
            setUnarchiveevid(evid);
          }
          : async () => {
            setArchiveevid(evid);
          };
        return (
          <Button variant="contained" color={color} onClick={onClick}>
            {text}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <FormControlLabel
        sx={{ paddingLeft: 2 }}
        control={
          <Checkbox
            checked={a1FilesChecked}
            onChange={async (ev) => {
              seta1FilesCheckde(ev.target.checked);
              if (ev.target.checked) {
                const a1list = await listAll(storageRef(getStorage(), `/archive`));
                setEventsWithA1Files(a1list.prefixes.map(e => e.name));
              }
            }}
            color="primary"
          />
        }
        label="Events that have files in A1 archive"
      />
      <div style={{ height: '80vh', width: "100%" }}>

        <DataGrid
          rows={eventslist}
          columns={columns}
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 100,
              },
            },
            sorting: {
              sortModel: [{ field: "starttime", sort: "desc" }],
            },
          }}
        />
        <Dialog
          open={Boolean(archiveevid || unarchiveevid)}
          maxWidth="md"
          fullWidth
        >
          <ArchiveProgressDialog
            archiveid={archiveevid}
            unarchiveid={unarchiveevid}
            onDone={() => {
              setArchiveevid(undefined);
              setUnarchiveevid(undefined);
            }}
          />
        </Dialog>
      </div>
    </>
  );
};

export default EventsManager;
